/**
 * UserDetailsModal Component
 *
 * Displays detailed information about a user including:
 * - User Details section (Email, Role, Agency, Viewing Rights, Last Active Date)
 *
 * @see Figma designs:
 * - Admin/Owner view: node-id=1881-34859
 */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import LytModal from '../ui-core/modal/LytModal';
import LytButton from '../library/button/LytButton';
import ButtonGroup from '../library/button-group/ButtonGroup';
import LytIcon from '../library/icon/LytIcon';
import Spinner from '../library/spinner/Spinner';
import { ICON_NAMES } from '../library/icon/icon-constants';
import {
    applyTypography,
    TYPOGRAPHY_VARIANTS,
} from '../styled/theme/typography-utils';
import { formatRole } from '../../utils/role-utils';
import { getTransferOwnershipLabel, getUpdateRoleAndPermissionsLabel } from 'src/constants/copy/team.copy';
import {
    getAgencyDisplayValue,
    getAgencyFieldLabel,
    getViewingRights,
    shouldShowWorkspaceDetails,
} from 'src/utils/user-details-utils';
import {
    getTeamMemberDetail,
    getLytTeamMemberDetail,
} from '../../services/requests/users-and-permissions/users/users';
import {
    selectedAgencyJurisdictionsSelector,
    selectedAgencyNameSelector,
    selectedAgencyTypeSelector,
} from 'src/store/user-info/user-info.selectors';
import { TEAM_CONTEXT } from './modal-role-options';

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
`;
const UserInfoSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-top: 24px;
`;
const SectionHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 8px;
`;
const SectionTitle = styled.h3`
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_1_BOLD)}
    letter-spacing: 0.14px;
    color: ${({ theme }) => theme?.text?.default};
    margin: 0;
`;
const SectionDescription = styled.p`
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_1_NORMAL)}
    line-height: 18px;
    color: ${({ theme }) => theme?.text?.default};
    margin: 0;
`;
const InfoGrid = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;
const InfoRow = styled.div`
    display: flex;
    align-items: ${({ $alignStart }) => ($alignStart ? 'flex-start' : 'center')};
    gap: 24px;
    min-height: 26px;
`;
const InfoLabelContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    width: 120px;
    flex-shrink: 0;
`;
const IconWrapper = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;
const InfoLabel = styled.span`
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_0_MEDIUM)}
    line-height: 16px;
    color: ${({ theme }) => theme?.text?.subtle};
    padding: 5px 0;
`;
const InfoValue = styled.span`
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_1_NORMAL)}
    line-height: 18px;
    color: ${({ theme }) => theme?.text?.subtle};
    flex: 1;
    min-width: 0;
    padding: 4px 0;
`;
const InfoListValue = styled.span`
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_1_NORMAL)}
    line-height: 18px;
    color: ${({ theme }) => theme?.text?.subtle};
`;
const InfoValueList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
    min-width: 0;
    padding: 4px 0;
`;
const LoadingContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px 0;
`;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Format a date string for display
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date string (e.g., "Jan 15, 2024")
 */
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

const InfoField = ({
    icon,
    label,
    value,
    testId = undefined,
}) => {
    const isMultiValue = Array.isArray(value);

    return (
    <InfoRow $alignStart={isMultiValue}>
        <InfoLabelContainer>
            <IconWrapper>
                <LytIcon name={icon} size={16} />
            </IconWrapper>
            <InfoLabel>{label}</InfoLabel>
        </InfoLabelContainer>
        {isMultiValue ? (
            <InfoValueList data-testid={testId}>
                {value.map((item) => (
                    <InfoListValue key={item}>{item}</InfoListValue>
                ))}
            </InfoValueList>
        ) : (
            <InfoValue data-testid={testId}>{value}</InfoValue>
        )}
    </InfoRow>
    );
};

InfoField.propTypes = {
    icon: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
    ]).isRequired,
    testId: PropTypes.string,
};

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * UserDetailsModal - Displays detailed user information
 *
 * For viewer roles: shows user info from table data (no API call)
 * For admin/owner/lyt roles: fetches detail from API to include changelog data
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Function} props.closeModal - Function to close modal with result (from ModalProvider)
 * @param {Object} props.user - User object containing user data (from table row)
 * @param {boolean} props.isAdmin - Whether the current viewer is an admin/owner/lyt user
 * @param {string} props.agencyId - The agency ID for the detail API call
 * @param {boolean} [props.showChangeRole] - Whether to show the Update Role & Permissions action
 * @param {Function} [props.onChangeRole] - Callback when Update Role & Permissions is clicked
 * @param {boolean} [props.showTransferOwnership] - Whether to show Transfer Ownership
 * @param {Function} [props.onTransferOwnership] - Callback when Transfer Ownership is clicked
 */
const UserDetailsModal = ({
    open = false,
    onClose = undefined,
    closeModal = undefined,
    user = {},
    isAdmin = false,
    agencyId = undefined,
    teamContext = undefined,
    showChangeRole = false,
    onChangeRole = undefined,
    showTransferOwnership = false,
    onTransferOwnership = undefined,
}) => {
    const [loading, setLoading] = useState(false);
    const [detailData, setDetailData] = useState(null);
    const agencyName = useSelector(selectedAgencyNameSelector);
    const agencyType = useSelector(selectedAgencyTypeSelector);
    const { list: agencyJurisdictions } = useSelector(selectedAgencyJurisdictionsSelector);

    // Fetch detail data for admin users when modal opens
    useEffect(() => {
        if (!open || !isAdmin) return;
        const userEmail = user?.email;
        if (!userEmail) return;
        let cancelled = false;
        setLoading(true);

        // Use the LYT-specific endpoint when opened from the LYT Team Management page
        const detailPromise =
            teamContext === TEAM_CONTEXT.LYT
                ? getLytTeamMemberDetail({
                      userEmail,
                  })
                : getTeamMemberDetail({
                      userEmail,
                      agencyId,
                  });
        detailPromise
            .then((response) => {
                if (!cancelled) {
                    setDetailData(response?.data || response);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    console.error('Failed to fetch user detail:', err);
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open, isAdmin, user?.email, agencyId, teamContext]);
    if (!open) return null;

    // Use detail data when available (admin flow), otherwise fall back to table row data
    const source = detailData || user;
    const userEmail = source?.email || 'N/A';
    const userRole = source?.role || source?.usertype || 'Unknown';
    const lastActiveDate = formatDate(
        source?.last_login_at_ts || source?.last_login || source?.lastLoginAt
    );
    const showWorkspaceDetails = shouldShowWorkspaceDetails(teamContext);
    const agencyFieldLabel = getAgencyFieldLabel({ role: userRole, agencyType });
    const agencyDisplayValue = getAgencyDisplayValue({ role: userRole, agencyName });
    const viewingRights = getViewingRights({
        role: userRole,
        userJurisdictions: source?.jurisdictions || [],
        agencyJurisdictions,
    });

    // Handle close action
    const handleClose = () => {
        closeModal?.({
            dismissed: true,
        }) || onClose?.();
    };

    const handleChangeRole = async () => {
        if (onChangeRole) {
            await onChangeRole(user);
        }
    };

    const handleTransferOwnership = async () => {
        if (onTransferOwnership) {
            await onTransferOwnership(user);
        }
    };

    // Modal footer actions
    const footerActions = (
        <ButtonGroup>
            {showTransferOwnership && onTransferOwnership && (
                <LytButton type="primary" onClick={handleTransferOwnership}>
                    {getTransferOwnershipLabel()}
                </LytButton>
            )}
            {showChangeRole && onChangeRole && (
                <LytButton type="primary" onClick={handleChangeRole}>
                    {getUpdateRoleAndPermissionsLabel()}
                </LytButton>
            )}
            <LytButton type="secondary" onClick={handleClose}>
                Close
            </LytButton>
        </ButtonGroup>
    );
    return (
        <LytModal
            open={open}
            title="User Details"
            onClose={handleClose}
            actions={footerActions}
            size="medium">
            <ModalContent>
                {loading ? (
                    <LoadingContainer>
                        <Spinner size="medium" />
                    </LoadingContainer>
                ) : (
                    <UserInfoSection>
                        <SectionHeader>
                            <SectionTitle>User Details</SectionTitle>
                            <SectionDescription>
                                See information about the user.
                            </SectionDescription>
                        </SectionHeader>
                        <InfoGrid>
                            <InfoField
                                icon={ICON_NAMES.StackedEmailFill}
                                label="Email Address"
                                value={userEmail}
                            />

                            <InfoField
                                icon={ICON_NAMES.BriefcaseFill}
                                label="Role"
                                value={formatRole(userRole)}
                            />

                            {showWorkspaceDetails && (
                                <>
                                    <InfoField
                                        icon={ICON_NAMES.AgencyFill}
                                        label={agencyFieldLabel}
                                        value={agencyDisplayValue}
                                        testId="agency-value"
                                    />
                                    <InfoField
                                        icon={ICON_NAMES.ViewingRightsFill}
                                        label="Viewing Rights"
                                        value={viewingRights.length > 0 ? viewingRights : ['N/A']}
                                        testId="viewing-rights-list"
                                    />
                                </>
                            )}

                            <InfoField
                                icon={ICON_NAMES.CalendarTodayFill}
                                label="Last Active Date"
                                value={lastActiveDate}
                            />
                        </InfoGrid>
                    </UserInfoSection>
                )}
            </ModalContent>
        </LytModal>
    );
};
UserDetailsModal.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    closeModal: PropTypes.func,
    user: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        email: PropTypes.string,
        firstname: PropTypes.string,
        lastname: PropTypes.string,
        first_name: PropTypes.string,
        last_name: PropTypes.string,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        usertype: PropTypes.string,
        role: PropTypes.string,
        last_login: PropTypes.string,
        last_login_at_ts: PropTypes.string,
        created_at: PropTypes.string,
        created_at_ts: PropTypes.string,
        jurisdictions: PropTypes.array,
        locations: PropTypes.array,
    }),
    isAdmin: PropTypes.bool,
    agencyId: PropTypes.string,
    teamContext: PropTypes.string,
    showChangeRole: PropTypes.bool,
    onChangeRole: PropTypes.func,
    showTransferOwnership: PropTypes.bool,
    onTransferOwnership: PropTypes.func,
};
export default UserDetailsModal;
